import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, RefreshCw } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  const activeCategory = searchParams.get('categoria') || '';

  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setRefreshing(true);
    
    try {
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`)
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds to get inventory updates
    const interval = setInterval(() => {
      fetchData(false);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchData]);

  const filteredProducts = products.filter(product => {
    const matchesCategory = !activeCategory || product.category === activeCategory;
    const matchesSearch = !searchTerm || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.barcode && product.barcode.includes(searchTerm));
    return matchesCategory && matchesSearch;
  });

  // Sort: products with discount first, then by name
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (a.discount && !b.discount) return -1;
    if (!a.discount && b.discount) return 1;
    return a.name.localeCompare(b.name);
  });

  const handleCategoryClick = (categoryId) => {
    if (categoryId === activeCategory) {
      searchParams.delete('categoria');
    } else {
      searchParams.set('categoria', categoryId);
    }
    setSearchParams(searchParams);
  };

  const getCategoryName = () => {
    if (!activeCategory) return 'Todos os Produtos';
    const category = categories.find(c => c.id === activeCategory);
    return category?.name || 'Produtos';
  };

  const handleRefresh = () => {
    fetchData(false);
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8" data-testid="products-page">
      {/* Header */}
      <div className="bg-primary text-white py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight">
                {getCategoryName()}
              </h1>
              <p className="text-white/70 mt-2">
                {sortedProducts.length} {sortedProducts.length === 1 ? 'produto disponível' : 'produtos disponíveis'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Search */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar produtos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-full border-border"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Button
            variant={!activeCategory ? 'default' : 'outline'}
            size="sm"
            className="rounded-full"
            onClick={() => handleCategoryClick('')}
            data-testid="filter-all"
          >
            Todos
          </Button>
          {categories.map(category => (
            <Button
              key={category.id}
              variant={activeCategory === category.id ? 'default' : 'outline'}
              size="sm"
              className="rounded-full"
              onClick={() => handleCategoryClick(category.id)}
              data-testid={`filter-${category.id}`}
            >
              {category.name}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="bg-card rounded-xl h-[300px] animate-pulse" />
            ))}
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="text-center py-16">
            <Filter className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">
              Nenhum produto encontrado
            </h3>
            <p className="text-muted-foreground">
              {products.length === 0 
                ? 'Aguarde, estamos atualizando nosso estoque!' 
                : 'Tente buscar por outro termo ou categoria'}
            </p>
            {searchTerm || activeCategory ? (
              <Button
                variant="outline"
                className="mt-4 rounded-full"
                onClick={() => {
                  setSearchTerm('');
                  searchParams.delete('categoria');
                  setSearchParams(searchParams);
                }}
                data-testid="clear-filters"
              >
                Limpar filtros
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 stagger-children">
            {sortedProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
